import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { impersonateUser } from '@adornis/users/db/a-user.js';
import { EntityVersions } from '@adornis/versioned-entity/client/EntityVersions.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import {
  deleteCompany,
  removeUserFromCompany,
  setCompanyStatus,
  subscribeAllCompanyUsers,
  getCompanyByID,
} from '../../../api/company.js';
import { subscribeToJobPositionsByCompany } from '../../../api/job-position.js';
import { JobPosition } from '../../../db/job-position.js';
import { openWarningDialog } from '../../components/warning-dialog.js';
import { mainRouter } from '../../mainRouter.js';
import { translateEnum } from '../../translate-enum.js';
import { adminRouter } from './admin-routes.js';

export const AdminCompanyDetail = stateful((props: { companyID: string }) => {
  // Bumped after each status flip so the company query refetches with the new isActive value.
  const [statusRev, setStatusRev] = useState(0);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const company = useQuery(
    async () =>
      await getCompanyByID(props.companyID)({ name: 1, address: 1, sector: 1, website: 1, services: 1, isActive: 1 }),
    [props.companyID, statusRev],
  );

  const handleToggleStatus = async (next: boolean) => {
    setTogglingStatus(true);
    setStatusError(null);
    try {
      await setCompanyStatus(props.companyID, next)();
      setStatusRev(statusRev + 1);
    } catch (e: any) {
      console.error('Failed to update company status', e);
      setStatusError(e?.message ?? m.admin_company_detail_status_update_failed());
    } finally {
      setTogglingStatus(false);
    }
  };

  const userDataViewOptions = buildFullOptions({
    class: User,
    operation: subscribeAllCompanyUsers,
    customColumns: [
      { columnName: m.admin_user_list_column_email(), fieldName: 'email' },
      { columnName: m.admin_user_list_column_first_name(), fieldName: 'firstName' },
      { columnName: m.admin_user_list_column_last_name(), fieldName: 'lastName' },
      {
        columnName: '',
        customID: '_options',
        // 16px default padding
        columnWidth: 'calc(2* 16px + 50px + 2 * var(--spacing))',
        renderItem: entity => html`
          <div class="flex flex-row items-center gap-2">
            <button
              class="btn btn-ghost btn-xs"
              title="${m.admin_company_detail_remove_user_tooltip()}"
              @click=${async (e: Event) => {
                e.stopPropagation();
                const confirmed = await openWarningDialog({
                  title: m.admin_company_detail_remove_user_title(),
                  message: m.admin_company_detail_remove_user_message(),
                  confirmText: m.admin_company_detail_remove_user_confirm(),
                  cancelText: m.cancel(),
                });
                if (!confirmed) return;
                try {
                  await removeUserFromCompany(entity._id!, props.companyID)();
                } catch (err) {
                  console.error('Failed to remove user from company', err);
                }
              }}
            >
              <span class="icon-[material-symbols--delete] text-error"></span>
            </button>
            <button
              class="btn btn-ghost btn-xs"
              title="${m.admin_company_detail_impersonate_user_title()}"
              @click=${async (e: Event) => {
                e.stopPropagation();
                const confirmed = await openWarningDialog({
                  title: m.admin_company_detail_impersonate_user_title(),
                  message: m.admin_company_detail_impersonate_user_message(),
                  confirmText: m.admin_company_detail_impersonate_user_confirm(),
                  cancelText: m.cancel(),
                });
                if (!confirmed) return;
                try {
                  await impersonateUser(entity._id!, User._class)();
                  mainRouter.goTo('root');
                  setTimeout(() => window.location.reload(), 100);
                } catch (err) {
                  console.error('Failed to impersonate user', err);
                }
              }}
            >
              <span class="icon-[material-symbols--person-alert]"></span>
            </button>
          </div>
        `,
      },
    ],
    isSortable: false,
    isEntryClickable: true,
    entryClickHandler: entity => {
      adminRouter.goTo('userDetail', { params: { userID: entity._id! } });
    },
    view: new View({ filter: new AdornisFilter({ search: props.companyID }) }),
  });

  const jobPositionDataViewOptions = buildFullOptions({
    class: JobPosition,
    operation: subscribeToJobPositionsByCompany,
    view: new View({ filter: new AdornisFilter({ search: props.companyID }) }),
    customColumns: [
      { columnName: m.job_form_title_label(), fieldName: 'title' },
      {
        columnName: m.job_form_employmentType_label(),
        fieldName: 'employmentType',
        renderItem: entity => html`${translateEnum('job_form_enum', entity.employmentType ?? undefined)}`,
      },
      { columnName: m.admin_company_detail_applications_column(), fieldName: 'numApplicants' },
      { columnName: m.job_form_availableSlots_label(), fieldName: 'availableSlots' },
    ],
    isEntryClickable: true,
    entryClickHandler: entity => {
      goTo(adminRouter.link('jobDetail', { params: { companyID: props.companyID } }) + '/' + entity._id);
    },
    isSortable: false,
  });

  if (company.loading)
    return html`
      <div class="p-6">
        <div class="hero min-h-[60vh] bg-base-200 rounded-box">
          <div class="hero-content text-center">
            <div class="flex flex-col items-center">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <p class="mt-4 opacity-70">${m.company_profile_loading()}</p>
            </div>
          </div>
        </div>
      </div>
    `;

  return html`
    <div class="p-8">
      <div class="flex flex-col justify-between items-center mb-6">
        <button @click=${() => adminRouter.goTo('root')} class="btn btn-ghost">
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.back_to_list()}
        </button>
        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-bold text-base-content">${company.value?.name}</h1>
          ${
            company.value?.isActive === false
              ? html`<span class="badge badge-ghost badge-lg">${m.company_status_inactive()}</span>`
              : nothing
          }
        </div>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex flex-row justify-end gap-2">
            <button
              class="btn btn-ghost"
              title="${m.show_history_tooltip()}"
              @click=${() =>
                (document.getElementById(`company-history-${props.companyID}`) as HTMLDialogElement)?.showModal()}
            >
              <span class="icon-[material-symbols--history]"></span>
              ${m.history()}
            </button>
            <button class="btn btn-outline self-end" @click=${async () =>
              adminRouter.goTo('editCompany', { params: { companyID: props.companyID } })}>
              <span class="icon-[material-symbols--edit]"></span>
              ${m.edit()}
            </button>
            <button
              class="btn btn-error"
              @click=${async () => {
                const confirmed = await openWarningDialog({
                  title: m.admin_company_detail_delete_company_title(),
                  message: m.admin_company_detail_delete_company_message(),
                  confirmText: m.delete_plain(),
                  cancelText: m.cancel(),
                });
                if (!confirmed) return;
                await deleteCompany(props.companyID)();
                adminRouter.goTo('root');
              }}
            >
              <span class="icon-[material-symbols--delete]"></span>
              ${m.delete_plain()}
            </button>
          </div>
          <h2 class="card-title text-2xl">${m.admin_company_detail_status_title()}</h2>
            ${statusError ? html`<div class="alert alert-error mt-2">${statusError}</div>` : nothing}
            <label class="label cursor-pointer justify-start gap-3 mt-2">
              <input
                type="checkbox"
                class="toggle toggle-primary"
                ?checked=${company.value?.isActive !== false}
                ?disabled=${togglingStatus}
                @change=${(e: Event) => handleToggleStatus((e.target as HTMLInputElement).checked)}
              />
              <span class="label-text">${m.admin_company_detail_status_active_label()}</span>
            </label>
            <p class="text-sm text-base-content/70 mt-1">
              ${
                company.value?.isActive === false
                  ? m.admin_company_detail_status_subtitle_inactive()
                  : m.admin_company_detail_status_subtitle_active()
              }
            </p>

            <div class="divider my-6"></div>

            <h2 class="card-title text-2xl mb-4">${m.company_details()}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-base-content">
              <div class="flex items-start">
                <span class="icon-[material-symbols--location-on] text-primary mt-1 mr-3"></span>
                <div>
                  <div class="font-bold">${m.address()}</div>
                  <div>${company.value?.address}</div>
                </div>
              </div>
              <div class="flex items-start">
                <span class="icon-[material-symbols--work] text-primary mt-1 mr-3"></span>
                <div>
                  <div class="font-bold">${m.sector()}</div>
                  <div>${company.value?.sector}</div>
                </div>
              </div>
              <div class="flex items-start">
                <span class="icon-[material-symbols--language] text-primary mt-1 mr-3"></span>
                <div>
                  <div class="font-bold">${m.website()}</div>
                  <a href="${company.value?.website}" target="_blank" class="link link-primary"
                    >${company.value?.website}</a
                  >
                </div>
              </div>
              <div class="flex items-start">
                <span class="icon-[material-symbols--build] text-primary mt-1 mr-3"></span>
                <div>
                  <div class="font-bold">${m.services()}</div>
                  <div>${company.value?.services}</div>
                </div>
              </div>
            </div>

            <div class="divider my-6"></div>

            <div class="flex justify-between items-center mb-4">
              <h2 class="card-title text-2xl">${m.admin_users()}</h2>
              <button
                @click=${() => adminRouter.goTo('createUserForCompany', { params: { companyID: props.companyID } })}
                class="btn btn-primary"
              >
                <span class="icon-[material-symbols--add]"></span>
                ${m.create_user()}
              </button>
            </div>
            ${DataTable({ options: userDataViewOptions.dataViewOptions })}

            <div class="divider my-6"></div>

            <h2 class="card-title text-2xl mb-4">${m.open_job_positions()}</h2>
            ${DataTable({ options: jobPositionDataViewOptions.dataViewOptions })}
          </div>
        </div>
      </div>

      <dialog id="company-history-${props.companyID}" class="modal">
        <div class="modal-box min-w-[800px]">
          <h3 class="font-bold text-lg mb-4">${m.history()}</h3>
          <div class="max-h-[70vh] overflow-auto">
            ${EntityVersions({ documentID: props.companyID })}
          </div>
          <div class="modal-action">
            <form method="dialog">
              <button class="btn">${m.close()}</button>
            </form>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button>${m.close()}</button>
        </form>
      </dialog>
    </div>
  `;
});
